import React, { useMemo } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  LineChart, 
  BarChart, 
  PieChart, 
  TrendingUp, 
  TrendingDown,
  ArrowRight,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { useTransactions } from '@/contexts/TransactionContext';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, BarChart as RechartsBarChart, Bar, XAxis, YAxis, Tooltip, LineChart as RechartsLineChart, Line, CartesianGrid, Legend } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const Analytics = () => {
  const { transactions, loading } = useTransactions();

  const {
    spendingData,
    monthlyData,
    totalIncome,
    totalExpenses,
    netSavings,
    incomeChange,
    expensesChange,
    savingsChange,
    aiInsights,
    topCategories,
    spendingTrends
  } = useMemo(() => {
    if (!transactions.length) {
      return {
        spendingData: [],
        monthlyData: [],
        totalIncome: 0,
        totalExpenses: 0,
        netSavings: 0,
        incomeChange: 0,
        expensesChange: 0,
        savingsChange: 0,
        aiInsights: [],
        topCategories: [],
        spendingTrends: []
      };
    }

    // Calculate spending by category
    const categorySpending = transactions.reduce((acc, transaction) => {
      acc[transaction.category] = (acc[transaction.category] || 0) + Math.abs(transaction.amount);
      return acc;
    }, {} as Record<string, number>);

    // Get top spending categories
    const topCategories = Object.entries(categorySpending)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([category, amount]) => ({
        category,
        amount,
        percentage: (amount / Object.values(categorySpending).reduce((a, b) => a + b, 0)) * 100
      }));

    const spendingData = Object.entries(categorySpending).map(([category, amount]) => ({
      name: category,
      value: amount
    }));

    // Calculate monthly data for the last 6 months
    const monthlyData = Array.from({ length: 6 }, (_, i) => {
      const date = subMonths(new Date(), i);
      const start = startOfMonth(date);
      const end = endOfMonth(date);

      const monthTransactions = transactions.filter(t => {
        const tDate = new Date(t.transaction_date);
        return tDate >= start && tDate <= end;
      });

      const income = monthTransactions
        .filter(t => t.amount > 0)
        .reduce((sum, t) => sum + t.amount, 0);

      const expenses = monthTransactions
        .filter(t => t.amount < 0)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);

      return {
        month: format(date, 'MMM'),
        income,
        expenses,
        savings: income - expenses
      };
    }).reverse();

    // Calculate spending trends
    const spendingTrends = monthlyData.map((month, index) => {
      const previousMonth = monthlyData[index - 1];
      const monthOverMonthChange = previousMonth 
        ? ((month.expenses - previousMonth.expenses) / previousMonth.expenses) * 100 
        : 0;

      return {
        ...month,
        change: monthOverMonthChange
      };
    });

    // Calculate totals and changes
    const currentMonth = startOfMonth(new Date());
    const lastMonth = subMonths(currentMonth, 1);

    const currentMonthTransactions = transactions.filter(t => {
      const tDate = new Date(t.transaction_date);
      return tDate >= currentMonth;
    });

    const lastMonthTransactions = transactions.filter(t => {
      const tDate = new Date(t.transaction_date);
      return tDate >= lastMonth && tDate < currentMonth;
    });

    const totalIncome = currentMonthTransactions
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);

    const totalExpenses = currentMonthTransactions
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    const lastMonthIncome = lastMonthTransactions
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);

    const lastMonthExpenses = lastMonthTransactions
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    const netSavings = totalIncome - totalExpenses;
    const lastMonthSavings = lastMonthIncome - lastMonthExpenses;

    const incomeChange = lastMonthIncome ? ((totalIncome - lastMonthIncome) / lastMonthIncome) * 100 : 0;
    const expensesChange = lastMonthExpenses ? ((totalExpenses - lastMonthExpenses) / lastMonthExpenses) * 100 : 0;
    const savingsChange = lastMonthSavings ? ((netSavings - lastMonthSavings) / lastMonthSavings) * 100 : 0;

    // Generate AI insights based on transaction data
    const aiInsights = [
      {
        title: "Spending Pattern Analysis",
        description: `Your ${topCategories[0].category.toLowerCase()} expenses are the highest at ${topCategories[0].percentage.toFixed(1)}%. Consider reviewing your spending in this category.`,
        icon: <TrendingUp className="h-5 w-5 text-red-500" />,
      },
      {
        title: "Savings Opportunity",
        description: expensesChange > 0 
          ? `Your expenses have increased by ${expensesChange.toFixed(1)}% compared to last month. Look for areas to reduce spending.`
          : `Your expenses have decreased by ${Math.abs(expensesChange).toFixed(1)}% compared to last month. Great job!`,
        icon: <TrendingDown className="h-5 w-5 text-green-500" />,
      },
      {
        title: "Budget Optimization",
        description: savingsChange > 0
          ? `Your savings have increased by ${savingsChange.toFixed(1)}% compared to last month. Keep up the good work!`
          : `Your savings have decreased by ${Math.abs(savingsChange).toFixed(1)}% compared to last month. Consider reviewing your budget.`,
        icon: <Sparkles className="h-5 w-5 text-blue-500" />,
      },
    ];

    return {
      spendingData,
      monthlyData,
      totalIncome,
      totalExpenses,
      netSavings,
      incomeChange,
      expensesChange,
      savingsChange,
      aiInsights,
      topCategories,
      spendingTrends
    };
  }, [transactions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Analytics & AI Insights</h1>
        <Button>
          <ArrowRight className="mr-2 h-4 w-4" />
          View Detailed Report
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100 text-green-600">
                <TrendingUp className="h-5 w-5" />
              </div>
              <h3 className="font-medium">Total Income</h3>
            </div>
            <div className={`flex items-center gap-1 ${incomeChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {incomeChange >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
              <span className="text-sm">{Math.abs(incomeChange).toFixed(1)}%</span>
            </div>
          </div>
          <p className="text-2xl font-bold">${totalIncome.toFixed(2)}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {topCategories.length > 0 && `Top category: ${topCategories[0].category} (${topCategories[0].percentage.toFixed(1)}%)`}
          </p>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-100 text-red-600">
                <TrendingDown className="h-5 w-5" />
              </div>
              <h3 className="font-medium">Total Expenses</h3>
            </div>
            <div className={`flex items-center gap-1 ${expensesChange <= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {expensesChange <= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
              <span className="text-sm">{Math.abs(expensesChange).toFixed(1)}%</span>
            </div>
          </div>
          <p className="text-2xl font-bold">${totalExpenses.toFixed(2)}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {topCategories.length > 0 && `Highest spending: ${topCategories[0].category} ($${topCategories[0].amount.toFixed(2)})`}
          </p>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                <Sparkles className="h-5 w-5" />
              </div>
              <h3 className="font-medium">Net Savings</h3>
            </div>
            <div className={`flex items-center gap-1 ${savingsChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {savingsChange >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
              <span className="text-sm">{Math.abs(savingsChange).toFixed(1)}%</span>
            </div>
          </div>
          <p className="text-2xl font-bold">${netSavings.toFixed(2)}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {topCategories.length > 0 && `Savings rate: ${((netSavings / totalIncome) * 100).toFixed(1)}%`}
          </p>
        </Card>
      </div>

      {/* AI Insights */}
      <Card className="p-6">
        <h2 className="font-medium mb-4">AI Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {aiInsights.map((insight, index) => (
            <div key={index} className="p-4 rounded-lg border">
              <div className="flex items-center gap-2 mb-2">
                {insight.icon}
                <h3 className="font-medium">{insight.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground">{insight.description}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Charts */}
      <Tabs defaultValue="spending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="spending">Spending by Category</TabsTrigger>
          <TabsTrigger value="monthly">Monthly Overview</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>
        <TabsContent value="spending" className="space-y-4">
          <Card className="p-6">
            <h3 className="font-medium mb-4">Spending Distribution</h3>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={spendingData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={150}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {spendingData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number) => `$${value.toFixed(2)}`}
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
                  />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="monthly" className="space-y-4">
          <Card className="p-6">
            <h3 className="font-medium mb-4">Monthly Income vs Expenses</h3>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value: number) => `$${value.toFixed(2)}`}
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
                  />
                  <Legend />
                  <Bar dataKey="income" name="Income" fill="#4ade80" />
                  <Bar dataKey="expenses" name="Expenses" fill="#f87171" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="trends" className="space-y-4">
          <Card className="p-6">
            <h3 className="font-medium mb-4">Spending Trends</h3>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsLineChart data={spendingTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value: number) => `${value.toFixed(1)}%`}
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="change" 
                    name="Month-over-Month Change" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                  />
                </RechartsLineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics; 